import useEventListener from '@use-it/event-listener';
import type { FunctionComponent, RefObject, TargetedEvent } from 'preact';
import { useCallback } from 'preact/hooks';

import type { TemplateFunction } from 'Components/types';
import { setAutocompleteQuery } from 'Core/actions/autocomplete';
import { autoFocusInsideProps } from 'Core/hooks/autofocus';
import { autocompleteRequestQuerySelector } from 'Core/selectors/autocomplete';
import { useDispatch, useSelector } from 'Core/store';
import { isTruthy } from 'Utils/guards';

export type Params = {
  name: string;
};

type Props = {
  template: TemplateFunction<Params>;
  name: string;
  onChange: (value: string) => void;
  onFocus?: () => void;
  rootRef: RefObject<HTMLElement>;
  inputRef: RefObject<HTMLInputElement>;
  enableAutoFocus?: boolean;
};

const SearchInput: FunctionComponent<Props> = ({
  template,
  name,
  onChange,
  onFocus,
  rootRef,
  inputRef,
  enableAutoFocus,
}) => {
  const dispatch = useDispatch();
  const query = useSelector(autocompleteRequestQuerySelector);

  useEventListener(
    'touchmove',
    useCallback(() => inputRef.current?.blur(), [inputRef]),
    rootRef.current?.querySelector<HTMLElement>('.cm_autocomplete'),
  );

  const component = template.call({ name });
  if (!component) {
    return null;
  }

  const { className: templateClassName, children: templateChildren, ...templateProps } = component.props;

  if (typeof templateChildren !== 'string' && templateChildren !== undefined) {
    console.error(
      'searchInput children expected to be either a string or nothing. Received:',
      templateChildren,
    );
    return null;
  }

  const props = {
    type: 'text',
    autoComplete: 'off',
    ...templateProps,
    className: [templateClassName, 'nav-root'].filter(isTruthy).join(' '),
    children: null,
    placeholder: templateChildren,
    value: query,
    onChange: (e: TargetedEvent<HTMLInputElement, Event>) => {
      const { value } = e.currentTarget;
      dispatch(setAutocompleteQuery(value));
      onChange(value);
    },
    onFocus,
    ref: inputRef,
  };

  if (enableAutoFocus) {
    return (
      <div {...autoFocusInsideProps}>
        <input {...props} />
      </div>
    );
  }

  return <input {...props} />;
};

export default SearchInput;
